import { useEffect, useMemo, useState } from "react";
import { Base } from "../framework/base";
import {
    OptionWorkTimeDefaultTimeZone,
    OptionWorkTimeDefaultTimeZoneEnabled,
} from "../models/common/consts";
import { EmployeeListItemDto } from "../models/employee/employee";
import { getEmmployeeGroupOptions } from "../services/employeeGroupService";
import { showApiError } from "../components/framework/formUtils";
import { ITypedOption, TypedOption } from "../models/common/typedOption";

// 'employees' parameter could be made optional to allow this hook to fetch employees
// But should be provided as a parameter when they have already been fetched
//  to avoid unnecessarya api calls.
export const useEmployeeGroupOptions = (
    employeeId: string,
    employees: EmployeeListItemDto[]
): [ITypedOption[], boolean] => {
    const [options, setOptions] = useState<ITypedOption[]>();
    const [loading, setLoading] = useState(true);

    const salaryGroupId = useMemo(
        () => employees?.find((e) => e.id === employeeId)?.salaryGroupId,
        [employeeId, employees]
    );

    useEffect(() => {
        if (!!salaryGroupId) {
            setLoading(true);
            getEmmployeeGroupOptions(salaryGroupId)
                .then(setOptions)
                .catch(showApiError)
                .finally(() => setLoading(false));
        }
    }, [salaryGroupId]);

    return [options, loading];
};

const useEmployeeGroupOptionValue = <T>(
    options: ITypedOption[],
    code: string
): T => {
    return useMemo(() => {
        const option = options?.find(
            (o) => o.code.toLowerCase() === code.toLowerCase()
        );

        if (!!option) {
            return <T>new TypedOption(option).getTypedValue();
        }

        return null;
    }, [options, code]);
};

// This could be modified to include other worktime related options in the future.
export const useWorkTimeDefaultTimezoneForEmployeeGroup = (
    employeeGroupId: string,
    employees: EmployeeListItemDto[]
): [string | null, boolean] => {
    const [options, loading] = useEmployeeGroupOptions(
        employeeGroupId,
        employees
    );

    const enabled = useEmployeeGroupOptionValue<boolean>(
        options,
        OptionWorkTimeDefaultTimeZoneEnabled
    );
    const tz = useEmployeeGroupOptionValue<string>(
        options,
        OptionWorkTimeDefaultTimeZone
    );

    return [(enabled && tz) || Base.timeZoneName(), loading];
};
